import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input, Table, DatePicker, message, ConfigProvider } from 'antd';
import { ref, get, remove } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import { MagnifyingGlass, Trash } from '@phosphor-icons/react';
import moment from 'moment';
import 'moment/locale/pl';
import plPL from 'antd/es/locale/pl_PL';
import './Wydatki.css'; // reuse same styles

const { RangePicker } = DatePicker;

const BonusPartnera = () => {
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;

  const [searchValue, setSearchValue] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [bonusData, setBonusData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAndDisplayData();
  }, [db]);

  const loadAndDisplayData = async () => {
    setLoading(true);
    try {
      const snapshot = await get(ref(db, '/drivers'));
      const drivers = snapshot.val();
      let bonuses = [];

      for (let driverId in drivers) {
        const driverData = drivers[driverId];
        if (driverData.bonus_partnera) {
          for (let bonusId in driverData.bonus_partnera) {
            bonuses.push({ ...driverData.bonus_partnera[bonusId], driverId, bonusId });
          }
        }
      }

      bonuses.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setBonusData(bonuses);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching bonus data:', error);
      message.error('Błąd podczas pobierania danych.');
    }
    setLoading(false);
  };

  const filteredData = useMemo(() => {
    const q = searchValue.toLowerCase();
    return bonusData.filter(b => {
      const matchesSearch = !q || Object.values(b).some(v => v && v.toString().toLowerCase().includes(q));
      let matchesDate = true;
      if (dateRange && dateRange[0] && dateRange[1]) {
        const start = dateRange[0].format('YYYY-MM-DD');
        const end = dateRange[1].format('YYYY-MM-DD');
        matchesDate = b.date >= start && b.date <= end;
      }
      return matchesSearch && matchesDate;
    });
  }, [bonusData, searchValue, dateRange]);

  const handleDelete = useCallback(async (bonusId, driverId) => {
    setLoading(true);
    try {
      await remove(ref(db, `/drivers/${driverId}/bonus_partnera/${bonusId}`));
      setBonusData(prev => prev.filter(b => b.bonusId !== bonusId));
      message.success('Bonus usunięty.');
    } catch (error) {
      console.error('Error deleting:', error);
      message.error('Błąd podczas usuwania.');
    }
    setLoading(false);
  }, [db]);

  const columns = useMemo(() => [
    {
      title: 'Kierowca',
      dataIndex: 'driverId',
      key: 'driverId',
      sorter: (a, b) => (a.driverId || '').localeCompare(b.driverId || ''),
      width: 140,
      render: (id) => <span className="wyd-mono">{id}</span>,
    },
    {
      title: 'Kwota',
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a, b) => parseFloat(a.amount || 0) - parseFloat(b.amount || 0),
      width: 110,
      align: 'right',
      render: (v) => <span className="wyd-amount">{v || '-'}</span>,
    },
    {
      title: 'Data',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => (a.date || '').localeCompare(b.date || ''),
      width: 110,
      render: (d) => <span style={{ fontSize: 12, color: '#6b7280' }}>{d || '-'}</span>,
    },
    {
      title: 'Powód',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Komentarz',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      render: (v) => <span style={{ color: '#9ca3af' }}>{v || '-'}</span>,
    },
    {
      title: '',
      key: 'action',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <button className="wyd-del-btn" onClick={() => handleDelete(record.bonusId, record.driverId)} title="Usuń">
          <Trash size={15} />
        </button>
      ),
    },
  ], [handleDelete]);

  return (
    <ConfigProvider locale={plPL}>
      <div>
        <div className="wyd-toolbar">
          <Input
            prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
            placeholder="Szukaj..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            style={{ flex: 2, minWidth: 150 }}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => { setDateRange(dates || null); setCurrentPage(1); }}
            format="YYYY-MM-DD"
            placeholder={['Od', 'Do']}
            style={{ flex: 1, minWidth: 200 }}
          />
        </div>

        <Table
          className="wyd-table"
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: rowsPerPage,
            total: filteredData.length,
            onChange: (p) => setCurrentPage(p),
            onShowSizeChange: (_, s) => { setRowsPerPage(s); setCurrentPage(1); },
            showSizeChanger: true,
            pageSizeOptions: ['15', '25', '50'],
            showTotal: (total, range) => `${range[0]}–${range[1]} z ${total}`,
            size: 'small',
          }}
          rowKey="bonusId"
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="middle"
        />
      </div>
    </ConfigProvider>
  );
};

export default BonusPartnera;
